import {
  AfterViewInit,
  Component,
  ChangeDetectorRef,
  Inject,
  ViewChild,
} from '@angular/core';
import { DialogService, DialogRef, DialogConfig, DIALOG_DATA } from '../dialog';
import { RefusalComponent } from '../reprovation/reprovation.component';
import { EmployeeMessageLimitService } from 'src/app/services/employee-message-limit.service';

import {
  Category,
  PreselectionOutcome,
  RefusalPreselection,
  SubmissionReprovation,
} from '../../shared/model/reprovation';
import {
  ResponseSubReason,
  ResponseReason,
} from '../../shared/model/refusal-reasons.model';

export interface DialogReprovationResult extends SubmissionReprovation {
  action: boolean;
  situationsError?: boolean;
  /**
   * What became of the preselection this dialog was opened with. Absent when it
   * was opened without one, or when the attempt never completed.
   */
  preselectionApplied?: PreselectionOutcome;
}

export interface DialogReprovationData {
  preselection?: RefusalPreselection;
}

@Component({
  selector: 'app-dialog-reprovation',
  templateUrl: './dialog-reprovation.component.html',
  styleUrls: ['./dialog-reprovation.component.scss'],
})
export class DialogReprovationComponent implements AfterViewInit {
  @ViewChild(RefusalComponent) refusal?: RefusalComponent;

  reprovation: SubmissionReprovation;

  // Read once: a getter would hand the form a new object on every change detection
  // pass, re-running its input setter for good.
  readonly preselection: RefusalPreselection | null;

  private preselectionApplied: PreselectionOutcome | null = null;

  /** The payload carries no validity, so the length limit is read off the child. */
  get confirmDisabled(): boolean {
    return !this.reprovation?.message || !!this.refusal?.message.invalid;
  }

  public static open(
    dialog: DialogService,
    data?: DialogReprovationData
  ): DialogRef<DialogReprovationComponent, DialogReprovationResult> {
    const config: DialogConfig<DialogReprovationData> = {
      width: '550px',
      maxWidth: 'calc(100% - 32px)',
      maxHeight: '90%',
      height: 'auto',
      autoFocus: false,
      closeOnNavigation: false,
      disableClose: true,
      data,
    };

    return dialog.open(DialogReprovationComponent, config);
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    public dialogRef: DialogRef<DialogReprovationComponent>,
    @Inject(DIALOG_DATA) data: DialogReprovationData | null,
    private employeeMessageLimit: EmployeeMessageLimitService
  ) {
    this.preselection = data?.preselection ?? null;
  }

  // The form does not know which screen hosts it, so the host reports.
  ngAfterViewInit(): void {
    if (this.refusal) {
      this.employeeMessageLimit.track(this.refusal.message, 'refusal_dialog');
    }
  }

  get reprovationConfirmation(): DialogReprovationResult {
    return {
      ...this.reprovation,
      action: true,
      ...(this.preselectionApplied === null
        ? {}
        : { preselectionApplied: this.preselectionApplied }),
    };
  }

  preselectionAppliedChanged(outcome: PreselectionOutcome): void {
    this.preselectionApplied = outcome;
  }

  reprovationChanged(reprovation: SubmissionReprovation): void {
    this.reprovation = reprovation;
    this.changeDetectorRef.detectChanges();
  }

  private setCategories(
    items: ResponseReason[] | ResponseSubReason[]
  ): Category[] {
    return items.map((reason: ResponseReason | ResponseSubReason) => {
      const children = (reason as ResponseReason)?.reasons;
      const category: Category = {
        id: reason.id,
        name: reason.label,
      };

      if (!!children) {
        category.children = this.setCategories(children);
      }

      return category;
    });
  }
}
